import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { LoadingSpinner } from "@/components/loading/LoadingSpinner";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/use-toast";
import { useConfirmationAlert } from "@/hook/confirmation-alert/hooks/actions";
import {
  useGetPriceRemarkParametersQuery,
  useUpdatePriceRemarkParametersMutation,
} from "@/services/price-remark/parameters";
import { PriceRemarkParameter } from "@/services/price-remark/types";

import { ParamsFormValues, paramsSchema } from "../data/schema";
import { ParametersAuditPanel } from "./audit-panel";

type ParamName = keyof ParamsFormValues;

interface ParamMeta {
  label: string;
  description: string;
  control: "date" | "number" | "switch";
}

const PARAM_META: Record<ParamName, ParamMeta> = {
  REM_DATA_LANCAMENTO: {
    label: "Data de lançamento (go-live)",
    description:
      "Lotes com data anterior não existem para o app. Alterar exige confirmação dupla.",
    control: "date",
  },
  REM_DIAS_RETROATIVOS_LOTE: {
    label: "Dias retroativos do lote",
    description: "Janela móvel: nada mais velho que N dias entra no retrato.",
    control: "number",
  },
  REM_EXPURGO_LEDGER_HABILITADO: {
    label: "Expurgo do ledger no coletor",
    description:
      "Libera a faxina do ledger no aparelho — nada é apagado no Oracle.",
    control: "switch",
  },
  REM_RETENCAO_LEDGER: {
    label: "Retenção do ledger (dias)",
    description:
      "Idade mínima para uma linha já enviada virar candidata ao expurgo local.",
    control: "number",
  },
  REM_RETENCAO_NOTIFICACOES: {
    label: "Retenção de notificações (dias)",
    description: "Janela do log de notificações do app (tabela descartável).",
    control: "number",
  },
};

const JANELA_FIELDS: ParamName[] = [
  "REM_DATA_LANCAMENTO",
  "REM_DIAS_RETROATIVOS_LOTE",
];
const MANUTENCAO_FIELDS: ParamName[] = [
  "REM_EXPURGO_LEDGER_HABILITADO",
  "REM_RETENCAO_LEDGER",
  "REM_RETENCAO_NOTIFICACOES",
];

const DEFAULT_VALUES: ParamsFormValues = {
  REM_DATA_LANCAMENTO: "1900-01-01",
  REM_DIAS_RETROATIVOS_LOTE: 1,
  REM_EXPURGO_LEDGER_HABILITADO: false,
  REM_RETENCAO_LEDGER: 1,
  REM_RETENCAO_NOTIFICACOES: 1,
};

function toFormValues(params: PriceRemarkParameter[]): ParamsFormValues {
  const byName = new Map(params.map((param) => [param.nome, param.valor]));
  const raw = (name: string) => byName.get(name) ?? "";
  return {
    REM_DATA_LANCAMENTO: raw("REM_DATA_LANCAMENTO") || "1900-01-01",
    REM_DIAS_RETROATIVOS_LOTE: Number(raw("REM_DIAS_RETROATIVOS_LOTE")) || 1,
    REM_EXPURGO_LEDGER_HABILITADO:
      raw("REM_EXPURGO_LEDGER_HABILITADO").toLowerCase() === "true",
    REM_RETENCAO_LEDGER: Number(raw("REM_RETENCAO_LEDGER")) || 1,
    REM_RETENCAO_NOTIFICACOES: Number(raw("REM_RETENCAO_NOTIFICACOES")) || 1,
  };
}

function toPayloadString(
  name: ParamName,
  value: string | number | boolean,
): string {
  if (name === "REM_EXPURGO_LEDGER_HABILITADO") return value ? "true" : "false";
  return String(value);
}

export function ParamsForm() {
  const { data: params, isLoading } = useGetPriceRemarkParametersQuery();
  const [updateParams, { isLoading: isSaving }] =
    useUpdatePriceRemarkParametersMutation();
  const { handleAlert } = useConfirmationAlert();

  const form = useForm<ParamsFormValues>({
    resolver: zodResolver(paramsSchema),
    defaultValues: DEFAULT_VALUES,
  });

  useEffect(() => {
    if (params) form.reset(toFormValues(params));
  }, [params, form]);

  const paramByName = new Map((params ?? []).map((p) => [p.nome, p]));

  const commit = async (payload: Record<string, string>) => {
    try {
      await updateParams(payload).unwrap();
      toast({
        variant: "success",
        description:
          "Parâmetros atualizados. Pode levar até 1 minuto na API e até o próximo sync no coletor.",
      });
    } catch (error) {
      const message =
        (error as { data?: { message?: string } })?.data?.message ??
        "Não foi possível salvar os parâmetros.";
      toast({ variant: "destructive", description: message });
    }
  };

  const onSubmit = (data: ParamsFormValues) => {
    const dirty = form.formState.dirtyFields as Partial<
      Record<ParamName, boolean>
    >;
    const payload: Record<string, string> = {};
    (Object.keys(data) as ParamName[]).forEach((name) => {
      if (dirty[name]) payload[name] = toPayloadString(name, data[name]);
    });

    if (Object.keys(payload).length === 0) return;

    if ("REM_DATA_LANCAMENTO" in payload) {
      handleAlert({
        isOpen: true,
        title: "Alterar a data de lançamento (go-live)?",
        description:
          "Muda o go-live de TODOS os coletores. Adiantar esconde trabalho pendente; atrasar pode despejar meses de lote no coletor.",
        confirmText: "Alterar go-live",
        isLoading: false,
        onConfirm: () => commit(payload),
      });
      return;
    }
    commit(payload);
  };

  if (isLoading) return <LoadingSpinner />;

  const dirtyCount = Object.keys(form.formState.dirtyFields).length;

  const renderField = (name: ParamName) => {
    const meta = PARAM_META[name];
    const param = paramByName.get(name);
    const error = form.formState.errors[name]?.message as string | undefined;

    return (
      <div key={name} className="space-y-1.5 border-b py-3 last:border-0">
        <div className="flex items-center justify-between gap-2">
          <Label className="font-medium text-gray-800">{meta.label}</Label>
          {param?.isDefault && (
            <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              padrão
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500">{meta.description}</p>
        <div className="flex items-center gap-3 pt-1">
          {meta.control === "switch" && (
            <Controller
              name={name}
              control={form.control}
              render={({ field }) => (
                <Switch
                  checked={Boolean(field.value)}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          )}
          {meta.control === "date" && (
            <Input
              type="date"
              className="h-8 w-[200px]"
              {...form.register(name)}
            />
          )}
          {meta.control === "number" && (
            <Input
              type="number"
              min={1}
              className="h-8 w-[140px]"
              {...form.register(name, { valueAsNumber: true })}
            />
          )}
          <span className="text-xs text-gray-400">
            atual: {param?.valor ?? "—"}
          </span>
        </div>
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-6 lg:col-span-2"
      >
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
          Gateway e login são geridos diretamente no banco (REMPARAM), por
          decisão de segurança.
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Janela de trabalho</CardTitle>
          </CardHeader>
          <CardContent>{JANELA_FIELDS.map(renderField)}</CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Manutenção do aparelho</CardTitle>
          </CardHeader>
          <CardContent>{MANUTENCAO_FIELDS.map(renderField)}</CardContent>
        </Card>

        <div className="sticky bottom-0 flex items-center justify-between gap-4 border-t bg-white py-3">
          <span className="text-sm text-gray-500">
            {dirtyCount > 0
              ? `${dirtyCount} alteração(ões) pendente(s)`
              : "Sem alterações"}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={dirtyCount === 0 || isSaving}
              onClick={() => params && form.reset(toFormValues(params))}
            >
              Descartar
            </Button>
            <Button type="submit" disabled={dirtyCount === 0 || isSaving}>
              Salvar alterações
            </Button>
          </div>
        </div>
      </form>

      <ParametersAuditPanel />
    </div>
  );
}

export default ParamsForm;
